/* eslint-disable eqeqeq */
import moment from "moment";

import { PosSubscriptionServices } from "globalConstants";
import { getDateWithoutTime } from "helpers";
import {
	getDefaultValuesForBackendPlatforms,
	getDefaultValuesForFrontEndPlatforms,
	getFrontEndPlatforms,
	mapAddressFromApi,
} from "mappers";
import {
	BillingStatusTypes,
	BillToTypes,
	GatewayConfigurationOption,
	NailsoftMerchantTypes,
	PaymentTypes,
	Processors,
	ShippingMethodTypes,
	TerminalConfigurationOption,
	TerminalFileBuildOptionTypes,
	TerminalProductNameTypes,
	TerminalProgramTypes,
	TerminalTypes,
	ZeroCostProgramTypes,
} from "types";
import { TerminalOption } from "types/api";
import { CadenceTypes } from "types/enums";
import {
	float6Normalizer,
	float2Normalizer,
	floatWithComma2Normalizer,
} from "validators";

export const getCopiedValues = (ownerPrincipalFormValues, merchantInfo) => {
	let result = {};
	if (ownerPrincipalFormValues) {
		if (ownerPrincipalFormValues?.isPrimaryContactTheSame === "true") {
			result = {
				owner1: {
					firstName:
						ownerPrincipalFormValues?.ownerPrincipalSection
							?.owners?.[0]?.firstName,
					lastName:
						ownerPrincipalFormValues?.ownerPrincipalSection
							?.owners?.[0]?.lastName,
					homeMobilePhone:
						ownerPrincipalFormValues?.ownerPrincipalSection
							?.owners?.[0]?.homeMobilePhone,
					emailAddress:
						ownerPrincipalFormValues?.ownerPrincipalSection
							?.owners?.[0]?.emailAddress,
				},
			};
		} else {
			result = {
				primaryContact: {
					firstName: ownerPrincipalFormValues?.firstName,
					lastName: ownerPrincipalFormValues?.lastName,
					phone: ownerPrincipalFormValues?.phone,
					email: ownerPrincipalFormValues?.email,
				},
			};
		}
	}

	if (merchantInfo) {
		result = merchantInfo;
	}

	return result;
};

export const getDefaultPrimaryContact = ({ data }) => {
	const isOwnerNotEmpty =
		data?.owner1?.firstName ||
		data?.owner1?.lastName ||
		data?.owner1?.homeMobilePhone;

	const isPrimaryContactNotEmpty =
		data?.primaryContact?.firstName ||
		data?.primaryContact?.lastName ||
		data?.primaryContact?.phone;

	const result = {
		contactFirstName: "",
		contactLastName: "",
		contactPhoneNumber: "",
		contactEmail: "",
	};

	if (isPrimaryContactNotEmpty) {
		result.contactFirstName = data?.primaryContact?.firstName;
		result.contactLastName = data?.primaryContact?.lastName;
		result.contactPhoneNumber = data?.primaryContact?.phone;
		result.contactEmail = data?.primaryContact?.email;
	} else if (isOwnerNotEmpty) {
		result.contactFirstName = data?.owner1?.firstName;
		result.contactLastName = data?.owner1?.lastName;
		result.contactPhoneNumber = data?.owner1?.homeMobilePhone;
		result.contactEmail = data?.owner1?.emailAddress;
	}

	return result;
};

const getDefaultValueForFileBuildOption = processorId => {
	if (processorId == Processors.TSYS.value) {
		return TerminalFileBuildOptionTypes.TsysStageOnlyVar.name;
	} else {
		return TerminalFileBuildOptionTypes.Other.name;
	}
};

type useGetTerminalQueryProps = {
	data?: any;
	merchantGeneralData?: any;
	applicationProcessor?: any;
	origin?: any;
	terminalAccessoryTypes?: { [key: string]: any }[];
};

const getDefaultValuesTerminalSolution = ({
	data,
	merchantGeneralData,
	applicationProcessor,
	origin,
	terminalAccessoryTypes,
}: useGetTerminalQueryProps) => {
	const processorId =
		merchantGeneralData?.processor?.id || applicationProcessor?.id;

	const defaultBackEndPlatformValues = getDefaultValuesForBackendPlatforms(
		+processorId,
	);
	const defaultFrontEndPlatforms = getDefaultValuesForFrontEndPlatforms(
		+processorId,
	);

	const options: TerminalOption[] = data?.terminal?.terminal_options;

	const terminalOptions = {
		avs: options?.includes("avs"),
		cvv: options?.includes("cvv"),
		multiMerchant: options?.includes("multi_merchant"),
		tipAdjust: options?.includes("tip_adjust"),
		serverNumber: options?.includes("server_number"),
		orderNumber: options?.includes("order_number"),
		amex: options?.includes("amex"),
		pinDebit: options?.includes("pin_debit"),
		externalPinPad: options?.includes("external_pin_pad"),
	};

	const frontEndPlatformsOnEdit = getFrontEndPlatforms(
		data?.front_end_platforms,
	);

	const frontEndPlatforms = data
		? frontEndPlatformsOnEdit
		: defaultFrontEndPlatforms;

	const [hours, minutes, median] = data?.terminal?.auto_close_at
		? moment(data?.terminal?.auto_close_at, "HH:mm:ss")
				.format("hh mm A")
				.split(" ")
		: [];

	const getMarketingServicesCost = () => {
		// This is a workaround to get data of the nail soft pos system
		// for migrated apps
		const posSystemCadence =
			data?.point_of_sale?.subscriptions?.pos_system?.is_subscribed ===
				true &&
			data?.point_of_sale?.subscriptions?.pos_system?.billing_cadence ===
				"annual"
				? "annually"
				: "monthly";

		return (
			floatWithComma2Normalizer(
				data?.point_of_sale?.subscriptions?.marketing_services?.cost,
			) ??
			(PosSubscriptionServices.MarketingServices.defaultPrice[
				posSystemCadence
			] ||
				PosSubscriptionServices.MarketingServices.defaultPrice.monthly)
		);
	};

	const getMarketingServicesCadence = () => {
		// This is a workaround to get data of the nail soft pos system
		// for migrated apps
		const posSystemCadence =
			data?.point_of_sale?.subscriptions?.pos_system?.is_subscribed ===
			true
				? CadenceTypes.getByName(
						data?.point_of_sale?.subscriptions?.pos_system
							?.billing_cadence,
				  )
				: undefined;

		return (
			CadenceTypes.getByName(
				data?.point_of_sale?.subscriptions?.marketing_services
					?.billing_cadence,
			) ||
			posSystemCadence ||
			CadenceTypes.Monthly
		);
	};

	const getQuantity = (
		quantity: string | null,
		defaultValue: string | null = "1",
	): string | null => {
		return quantity === null ? null : quantity ?? defaultValue;
	};

	const addressData = mapAddressFromApi(
		data?.shipping?.address || merchantGeneralData?.shippingAddress,
	);

	const defaultValues = {
		isFileBuildOnly: data?.is_file_build_only_solution_type,
		origin: data?.origin || origin,
		id: data?.id,
		number: data?.number,
		updatedAt: data?.updated_at,
		updatedById: data?.updated_by_id,
		updatedBy: data?.updated_by,

		isTerminal: data?.terminal ? true : false,
		///terminalAccessories: was moved from terminal for RC-8776
		terminalAccessories:
			data?.terminal?.terminal_accessories?.map(item => {
				return {
					id: item.id,
					type: terminalAccessoryTypes?.find(
						m => m.name === item.type,
					),
					otherName: item.other_name,
					price: float2Normalizer(item.price) || null,
					serialNumber: item.serial_number,
				};
			}) || [],
		terminal: {
			quantity: data?.quantity || "1",
			manufacturer: data?.terminal?.manufacturer || "",
			model: data?.terminal?.model || "",
			terminalDevices:
				data?.terminal?.terminal_devices?.map(d => {
					return {
						deviceId: d?.id,
						typeId: d?.type_id,
						serialNumber: d?.serial_number,
						name: d?.type_name,
					};
				}) || [],
			terminalConfigurationOption:
				data?.terminal?.terminal_configuration_option ||
				TerminalConfigurationOption.NewPurchase.name,
			terminalType: Number.isInteger(data?.terminal?.terminal_type)
				? TerminalTypes.getByValue(data?.terminal?.terminal_type)
				: TerminalTypes.Purchase,
			manufacturerId: data?.terminal?.manufacturer_id || "",
			otherManufacturerName:
				data?.terminal?.other_manufacturer_name || "",
			modelId: data?.terminal?.model_id || "",
			otherModelName: data?.terminal?.other_model_name || "",
			pricePerTerminal:
				float2Normalizer(data?.terminal?.price_per_terminal) || "0.00",
			dvid: data?.terminal?.dvid || "",
			simNumber: data?.terminal?.sim_number || "",
			cloverTerminalSettings: {
				cloverId:
					data?.terminal?.clover_terminal_settings?.clover_id || "",
				username:
					data?.terminal?.clover_terminal_settings?.username || "",
				goMonthlyFee:
					data?.terminal?.clover_terminal_settings?.go_monthly_fee ||
					"6.000000",
				servicesPerDeviceFee:
					data?.terminal?.clover_terminal_settings
						?.services_per_device_fee || "",
			},
			terminalOptions: terminalOptions || {},
			industryType: data?.terminal?.industry_type?.toString() || "1",
			connectionPrimary:
				data?.terminal?.connection_primary?.toString() || "1",
			connectionBackup:
				data?.terminal?.connection_backup?.toString() || "3",
			isAutoCloseEnabled:
				data?.terminal?.is_auto_close_enabled?.toString() || "false",
			autoCloseAtHours: hours,
			autoCloseAtMinutes: minutes,
			autoCloseAtMedian: {
				name: median || "AM",
				value: median || "AM",
			},
			isTransarmorEnabled:
				data?.terminal?.is_transarmor_enabled?.toString() || "false",
			transarmorPerTransactionFee:
				data?.terminal?.transarmor_per_transaction_fee || "",
			transarmorMonthlyFee: data?.terminal?.transarmor_monthly_fee
				? float6Normalizer(data?.terminal?.transarmor_monthly_fee)
				: "5.000000",

			isTsysTokenizationEnabled:
				data?.terminal?.is_tsys_tokenization_enabled?.toString() ||
				"false",
			fileBuild: {
				fileBuildOption:
					data?.file_build?.file_build_option ||
					getDefaultValueForFileBuildOption(processorId),
				fileType: data?.file_build?.file_type || "",
			},
			monthRentalPrice: data?.terminal?.month_rental_price || "0",
			equipmentInsurance: data?.terminal?.equipment_insurance || "0",
			shippingAndHandling: data?.terminal?.shipping_and_handling || "0",
			estimatedTax: data?.terminal?.estimated_tax || "0",
			stateTax: data?.terminal?.state_tax || "0",
			// Backend & frontend platforms
			backEndPlatform:
				data?.back_end_platform ||
				defaultBackEndPlatformValues?.backEndPlatform,
			otherBackEndPlatformName: data?.other_back_end_platform_name || "",
			otherFrontEndPlatformName:
				data?.other_front_end_platform_name || "",
			...frontEndPlatforms,
		},

		terminalProgramType: data?.terminal_program_type
			? TerminalProgramTypes.getByValue(data?.terminal_program_type)
			: TerminalProgramTypes.Normal,
		applicationId: data?.application_id || "",
		merchantId: data?.merchant_id || "",

		status: data?.status || "",
		tidVNumber: data?.tid_v_number || "",
		otherFrontEndPlatformName: data?.other_front_end_platform_name || "",
		frontEndMid: data?.front_end_mid || "",
		backEndPlatform:
			data?.back_end_platform ||
			defaultBackEndPlatformValues?.backEndPlatform,
		otherBackEndPlatformName: data?.other_back_end_platform_name || "",
		...frontEndPlatforms,

		isGateway: data?.gateway ? true : false,
		gateway: {
			gatewayConfigurationOption:
				data?.gateway?.gateway_configuration_option ||
				GatewayConfigurationOption.NewGateway.name,
			gatewayId: data?.gateway?.gateway_id || "",
			gatewayName: data?.gateway?.gateway_name_id
				? {
						name: data?.gateway?.gateway_name,
						id: data?.gateway?.gateway_name_id,
				  }
				: "",
			otherGatewayName: data?.gateway?.other_gateway_name || "",
			subdomain: data?.gateway?.subdomain || "",
			username: data?.gateway?.username || "",
			hostPassword: data?.gateway?.host_password || "",
			displayName: data?.gateway?.display_name || "",
			isLevel2And3DataEnabled:
				data?.gateway?.is_level_2_and_3_data_enabled?.toString() ||
				"false",
			isCashDiscountEnabled:
				data?.gateway?.is_cash_discount_enabled?.toString() || "false",
			isCustomerVaultOrRecurringBilling:
				data?.gateway?.is_customer_vault_or_recurring_billing?.toString() ||
				"true",
			monthlyFee: data?.gateway?.monthly_fee || "0.000000",
			transactionFee: data?.gateway?.transaction_fee || "0.000000",
			level3PerTransactionFee:
				data?.gateway?.level_3_per_transaction_fee || "0.000000",
			vaultMonthlyFee: data?.gateway?.vault_monthly_fee || "0.000000",
			setupFee: data?.gateway?.setup_fee || "0.000000",

			cardNetworkTokenizationSetupFee:
				data?.gateway?.card_network_tokenization_setup_fee ||
				"0.000000",
			cardNetworkTokenizationMonthlyFee:
				data?.gateway?.card_network_tokenization_monthly_fee ||
				"0.000000",
			cardNetworkPerTokenizationEventFee:
				data?.gateway?.card_network_per_tokenization_event_fee ||
				"0.000000",

			isTransarmorEnabled:
				data?.gateway?.is_transarmor_enabled?.toString() || "false",
			isTsysTokenizationEnabled:
				data?.gateway?.is_tsys_tokenization_enabled?.toString() ||
				"false",
			transarmorPerTransactionFee:
				data?.gateway?.transarmor_per_transaction_fee || "",
			transarmorMonthlyFee: data?.gateway?.transarmor_monthly_fee
				? float6Normalizer(data?.gateway?.transarmor_monthly_fee)
				: "5.000000",
			fileBuild: {
				fileBuildOption:
					data?.file_build?.file_build_option ||
					getDefaultValueForFileBuildOption(processorId),
				fileType: data?.file_build?.file_type || "",
			},
			// Backend & frontend platforms
			backEndPlatform:
				data?.back_end_platform ||
				defaultBackEndPlatformValues?.backEndPlatform,
			otherBackEndPlatformName: data?.other_back_end_platform_name || "",
			otherFrontEndPlatformName:
				data?.other_front_end_platform_name || "",
			...frontEndPlatforms,
		},
		isPointOfSale: data?.point_of_sale ? true : false,
		pointOfSale: {
			product:
				TerminalProductNameTypes.getByName(
					data?.point_of_sale?.product,
				) || "",
			nameIfOther: data?.point_of_sale?.name_if_other || "",
			authorizationVersion:
				data?.point_of_sale?.authorization_version || "",
			isTransarmorEnabled:
				data?.point_of_sale?.is_transarmor_enabled?.toString() ||
				"false",
			transarmorPerTransactionFee:
				data?.point_of_sale?.transarmor_per_transaction_fee || "",
			transarmorMonthlyFee: data?.point_of_sale?.transarmor_monthly_fee
				? float6Normalizer(data?.point_of_sale?.transarmor_monthly_fee)
				: "5.000000",
			isTsysTokenizationEnabled:
				data?.point_of_sale?.is_tsys_tokenization_enabled?.toString() ||
				"false",
			subscriptions: {
				posSystem: {
					billingCadence:
						CadenceTypes.getByName(
							data?.point_of_sale?.subscriptions?.pos_system
								?.billing_cadence,
						) || CadenceTypes.Monthly,
					cost:
						floatWithComma2Normalizer(
							data?.point_of_sale?.subscriptions?.pos_system
								?.cost,
						) ??
						PosSubscriptionServices.PosSystem.defaultPrice.monthly,
					quantity: getQuantity(
						data?.point_of_sale?.subscriptions?.pos_system
							?.quantity,
					),
					isSubscribed:
						data?.point_of_sale?.subscriptions?.pos_system
							?.is_subscribed ?? true,
				},
				marketingServices:
					data?.point_of_sale?.subscriptions?.marketing_services ===
					null
						? null
						: {
								billingCadence: getMarketingServicesCadence(),
								cost: getMarketingServicesCost(),
								quantity: getQuantity(
									data?.point_of_sale?.subscriptions
										?.marketing_services?.quantity,
								),
								isSubscribed:
									data?.point_of_sale?.subscriptions
										?.marketing_services?.is_subscribed ??
									false,
						  },
				ipPhoneService: {
					billingCadence:
						CadenceTypes.getByName(
							data?.point_of_sale?.subscriptions?.ip_phone_service
								?.billing_cadence,
						) || CadenceTypes.Monthly,
					cost:
						floatWithComma2Normalizer(
							data?.point_of_sale?.subscriptions?.ip_phone_service
								?.cost,
						) ??
						PosSubscriptionServices.IpPhoneService.defaultPrice
							.monthly.one,
					quantity: getQuantity(
						data?.point_of_sale?.subscriptions?.ip_phone_service
							?.quantity,
					),
					isSubscribed:
						data?.point_of_sale?.subscriptions?.ip_phone_service
							?.is_subscribed ?? false,
				},
				tapToPay: {
					billingCadence:
						CadenceTypes.getByName(
							data?.point_of_sale?.subscriptions?.tap_to_pay
								?.billing_cadence,
						) || CadenceTypes.Monthly,
					cost:
						floatWithComma2Normalizer(
							data?.point_of_sale?.subscriptions?.tap_to_pay
								?.cost,
						) ??
						PosSubscriptionServices.TapToPay.defaultPrice.monthly,
					quantity: getQuantity(
						data?.point_of_sale?.subscriptions?.tap_to_pay
							?.quantity,
					),
					isSubscribed:
						data?.point_of_sale?.subscriptions?.tap_to_pay
							?.is_subscribed ?? false,
				},
				salonInTheBox:
					data?.point_of_sale?.subscriptions?.salon_in_the_box ===
					null
						? null
						: {
								billingCadence:
									CadenceTypes.getByName(
										data?.point_of_sale?.subscriptions
											?.salon_in_the_box?.billing_cadence,
									) || CadenceTypes.OneTime,
								cost:
									floatWithComma2Normalizer(
										data?.point_of_sale?.subscriptions
											?.salon_in_the_box?.cost,
									) ??
									PosSubscriptionServices.SalonInTheBox
										.defaultPrice.oneTime,
								quantity: getQuantity(
									data?.point_of_sale?.subscriptions
										?.salon_in_the_box?.quantity,
								),
								isSubscribed:
									data?.point_of_sale?.subscriptions
										?.salon_in_the_box?.is_subscribed ??
									false,
						  },
				smsService: {
					billingCadence:
						CadenceTypes.getByName(
							data?.point_of_sale?.subscriptions?.sms_service
								?.billing_cadence,
						) || CadenceTypes.Monthly,
					cost:
						floatWithComma2Normalizer(
							data?.point_of_sale?.subscriptions?.sms_service
								?.cost,
						) ??
						PosSubscriptionServices.SmsService.defaultPrice.monthly,
					quantity: getQuantity(
						data?.point_of_sale?.subscriptions?.sms_service
							?.quantity,
					),
					isSubscribed:
						data?.point_of_sale?.subscriptions?.sms_service
							?.is_subscribed ?? false,
				},
				smsMessage: {
					billingCadence:
						CadenceTypes.getByName(
							data?.point_of_sale?.subscriptions?.sms_message
								?.billing_cadence,
						) || CadenceTypes.Consumption,
					cost:
						floatWithComma2Normalizer(
							data?.point_of_sale?.subscriptions?.sms_message
								?.cost,
						) ??
						PosSubscriptionServices.SmsMessage.defaultPrice
							.consumption,
					quantity: getQuantity(
						data?.point_of_sale?.subscriptions?.sms_message
							?.quantity,
						null,
					),
					isSubscribed:
						data?.point_of_sale?.subscriptions?.sms_message
							?.is_subscribed ?? false,
				},
				smsSetup: {
					billingCadence:
						CadenceTypes.getByName(
							data?.point_of_sale?.subscriptions?.sms_setup
								?.billing_cadence,
						) || CadenceTypes.OneTime,
					cost:
						floatWithComma2Normalizer(
							data?.point_of_sale?.subscriptions?.sms_setup?.cost,
						) ??
						PosSubscriptionServices.SmsSetup.defaultPrice.oneTime,
					quantity: getQuantity(
						data?.point_of_sale?.subscriptions?.sms_setup?.quantity,
					),
					isSubscribed:
						data?.point_of_sale?.subscriptions?.sms_setup
							?.is_subscribed ?? false,
				},
				discount:
					data?.point_of_sale?.subscriptions?.discount || "0.00",
				totalPrice:
					data?.point_of_sale?.subscriptions?.total_price || "0.00",
			},
			merchantType:
				NailsoftMerchantTypes.getByName(
					data?.point_of_sale?.merchant_type,
				) || "",
			// Backend & frontend platforms
			backEndPlatform:
				data?.back_end_platform ||
				defaultBackEndPlatformValues?.backEndPlatform,
			otherBackEndPlatformName: data?.other_back_end_platform_name || "",
			otherFrontEndPlatformName:
				data?.other_front_end_platform_name || "",
			...frontEndPlatforms,
		},
		paymentMethods: (data?.billings?.length > 0 &&
			data?.billings?.map(items => {
				return {
					billToInfo: {
						billTo: items?.bill_to?.toString() || "1",
						amountToBeBilled: float6Normalizer(
							items?.amount_to_be_billed,
						)?.toString(),
					},
					paymentTypeInfo: {
						paymentType:
							items?.payment_type !== "none" &&
							items?.payment_type !== null
								? items?.payment_type
								: PaymentTypes.ach.name,
						billingStatus: items?.billing_status
							? BillingStatusTypes.getByName(
									items?.billing_status,
							  )
							: "",
						invoiceDate: items?.invoice_date
							? getDateWithoutTime(items?.invoice_date)
							: "",
						paymentDate: items?.payment_date
							? getDateWithoutTime(items?.payment_date)
							: "",
					},
					paymentMethodId: items?.payment_method_id || "",
					paymentMethod: items?.payment_method_id || "",
					paymentMethodChosenId: items?.payment_method_id || "",
				};
			})) || [
			{
				billToInfo: {
					billTo: BillToTypes.merchant.value?.toString(),
					amountToBeBilled: "",
				},
				paymentTypeInfo: {
					paymentType: PaymentTypes.ach.name,
					billingStatus: BillingStatusTypes.pending,
					invoiceDate: "",
					paymentDate: "",
				},
				paymentMethodId: "",
				paymentMethod: "",
				paymentMethodChosenId: "",
			},
		],
		shipping: {
			...addressData,
			shippingMethod:
				data?.shipping?.shipping_method?.toString() ||
				ShippingMethodTypes.none.value?.toString(),
			phoneNumber: data?.shipping?.phone_number || "",
			orderNumber: data?.shipping?.order_number || "",
			vendor: data?.shipping?.vendor || "",
			carrier: data?.shipping?.carrier || "",
			trackingNumber: data?.shipping?.tracking_number || "",
			orderedAt: data?.shipping?.ordered_at
				? getDateWithoutTime(data?.shipping?.ordered_at)
				: "",
			shippedAt: data?.shipping?.shipped_at
				? getDateWithoutTime(data?.shipping?.shipped_at)
				: "",
			deliveredAt: data?.shipping?.delivered_at
				? getDateWithoutTime(data?.shipping?.delivered_at)
				: "",
			shouldCopyShippingAddressToMerchant:
				data?.shipping?.should_copy_shipping_address_to_merchant ||
				false,
		},
		fileBuild: {
			fileBuildOption:
				data?.file_build?.file_build_option ||
				getDefaultValueForFileBuildOption(processorId),
			fileType: data?.file_build?.file_type || "",
			dlid: data?.file_build?.dlid || "",
			datawireId: data?.file_build?.datawire_id || "",
			isIntegratedWithTsys: data?.file_build?.is_integrated_with_tsys,
			tsysTerminalSettings: {
				storeNumber:
					data?.file_build?.tsys_terminal_settings?.store_number ||
					"",
				terminalNumber:
					data?.file_build?.tsys_terminal_settings?.terminal_number ||
					"",
				sendEmail:
					data?.file_build?.tsys_terminal_settings?.send_email ||
					false,
				deviceId:
					data?.file_build?.tsys_terminal_settings?.device_id || "",
				userId: data?.file_build?.tsys_terminal_settings?.user_id || "",
				password:
					data?.file_build?.tsys_terminal_settings?.password || "",
			},
		},
		installation: {
			terminalInstall:
				data?.installation?.terminal_install?.toString() || "1",
			bestTimeToCall: data?.installation?.best_time_to_call || "am",
			contactFirstName: data?.installation?.contact_first_name || "",
			contactLastName: data?.installation?.contact_last_name || "",
			contactPhoneNumber: data?.installation?.contact_phone_number || "",
			contactEmail: data?.installation?.contact_email || "",
			notes: data?.installation?.notes || "",
			uninstallAt: data?.installation?.uninstall_at
				? getDateWithoutTime(data?.installation?.uninstall_at)
				: "",
			installAt: data?.installation?.install_at
				? getDateWithoutTime(data?.installation?.install_at)
				: "",
		},
		term: data?.term || "",
		applicationName: data?.application_name || "",
		// For Application Entity
		fileBuildOnlyQuantity:
			(data?.file_build_only_quantity !== null ||
				data?.file_build_only_quantity !== undefined) &&
			data?.file_build_only_quantity?.toString(),
		fileBuildOnlyVarRecipientEmail:
			data?.file_build_only_var_recipient_email || "",
		fileBuildOptionForApp:
			TerminalFileBuildOptionTypes.TsysStageOnlyVar.name,
		zeroCostProgramType: (
			data?.zero_cost_program_type || ZeroCostProgramTypes.None.value
		).toString(),
		zeroCostAmount: data?.zero_cost_amount || "0.00",
		// Bill to section on Application Entity
		billTo: data?.bill_to?.toString() || "1",
		paymentType: data?.payment_type || PaymentTypes.ach.name,
		paymentMethodId: data?.payment_method_id || "",
		paymentMethod: data?.payment_method_id || "",
		paymentMethodChosenId: data?.payment_method_id || "",
		amountToBeBilled: "",
	};

	return defaultValues;
};

export default getDefaultValuesTerminalSolution;
